import React, { useState } from 'react';
import axios from 'axios';
import { Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success('Thank you for contacting us! We will get back to you soon.');
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please call our office directly at (989) 791-4100.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section section-alt" data-testid="contact-section">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: 'var(--sage-dark)' }}>Contact Us</h2>
            <p className="text-lg text-gray-700">
              Have questions? We&apos;re here to help. Reach out to us and we&apos;ll respond as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card>
              <CardContent className="p-8">
                <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: 'rgba(139, 111, 71, 0.1)', borderLeft: '4px solid var(--earth-accent)' }}>
                  <p className="text-sm text-gray-700">
                    <strong style={{ color: 'var(--sage-dark)' }}>Please do not include sensitive medical information in this form.</strong> For medical questions or to share health information, please call our office directly at <a href="tel:9897914100" className="font-semibold hover:underline" style={{ color: 'var(--earth-accent)' }}>(989) 791-4100</a>.
                  </p>
                </div>
                <form onSubmit={handleContactSubmit} data-testid="contact-form">
                  <div className="form-group">
                    <label className="form-label">Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      data-testid="contact-name-input"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      data-testid="contact-email-input"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      data-testid="contact-phone-input"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message *</label>
                    <textarea
                      className="form-textarea"
                      data-testid="contact-message-input"
                      rows="4"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                    ></textarea>
                  </div>
                  <Button
                    type="submit"
                    className="w-full text-white"
                    style={{ backgroundColor: 'var(--earth-accent)' }}
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--sage-dark)' }}>Call Us</h3>
                  <div className="space-y-3">
                    <a href="tel:9897914100" className="flex items-center space-x-3 font-semibold hover:underline" style={{ color: 'var(--earth-accent)' }}>
                      <Phone className="w-5 h-5" />
                      <span>(989) 791-4100 - Saginaw</span>
                    </a>
                    <a href="tel:9894803470" className="flex items-center space-x-3 font-semibold hover:underline" style={{ color: 'var(--earth-accent)' }}>
                      <Phone className="w-5 h-5" />
                      <span>(989) 480-3470 - Frankenmuth</span>
                    </a>
                    <a href="mailto:saginawvalleymedical@yahoo.com" className="flex items-center space-x-3 text-gray-700 hover:underline break-all">
                      <Mail className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--earth-accent)' }} />
                      <span>saginawvalleymedical@yahoo.com</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

              <Card style={{ backgroundColor: 'rgba(139, 111, 71, 0.1)', borderColor: 'var(--earth-accent)' }}>
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--sage-dark)' }}>Emergency?</h3>
                  <p className="text-sm text-gray-700">
                    For medical emergencies, please call 911 or visit your nearest emergency room immediately.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
